import React from "react";
import clsx from "clsx";
import { Sparkles } from "lucide-react";
import { BusinessReport, BusinessReportPrompt, VariablesDict } from "@/modules/snapshots/businessReports.types";
import { GenerateSnapshotCollectionModal } from "../modals/generateSnapshotCollectionModal";
import { useToggle } from "react-use";
import { Item } from "@/types/fse";
import { InjectVariablesModal } from "../modals/injectVariablesModal";
import { useCurrentPdf } from "@/views/chat/hooks/useCurrentPdf";
import { useCreateBusinessReportOutcomeMutation } from "@/modules/snapshots/businessReports.api";
import { useNavigate } from "react-router";
import * as analytics from "@/services/analytics";

interface Props {
  selectedBusinessReport: BusinessReport;
  fsEntries: Item[];
  prompts: BusinessReportPrompt[];
}

export const BusinessReportsPageGenerate: React.FC<Props> = ({ fsEntries, prompts, selectedBusinessReport }) => {
  const { selectedFiles } = useCurrentPdf();
  const [createBusinessReportOutcome, { isLoading }] = useCreateBusinessReportOutcomeMutation();
  const navigate = useNavigate();
  const selectedFsEntries = fsEntries.filter(row => selectedFiles.includes(row.id));

  const [isOpen, toggleIsOpen] = useToggle(false);
  const [showVariablesModal, setShowVariablesModal] = React.useState(false);
  const [variables, setVariables] = React.useState<VariablesDict>({});
  const handleClick = () => {
    if (!selectedBusinessReport.variables.length) {
      generateBusinessReportOutcome();
    } else {
      setShowVariablesModal(true);
    }
  };

  const onVariablesModalClose = () => {
    setShowVariablesModal(false);
  };

  const generateReport = (newVariables: VariablesDict) => {
    setVariables(newVariables);
    generateBusinessReportOutcome();
    setShowVariablesModal(false);
  };

  const generateBusinessReportOutcome = async () => {
    if (selectedFsEntries.length === 1) {
      const result = await createBusinessReportOutcome({
        fs_entries: selectedFsEntries.map(row => row.id),
        name: "",
        business_report_id: selectedBusinessReport.id,
        business_report_prompts: prompts.map(prompt => ({
          order: prompt.order,
          business_report_prompt_id: prompt.id,
          prompt: prompt.prompt,
          name: prompt.name,
        })),
        variables: variables || {},
      }).unwrap();

      analytics.snapshotCreate(selectedBusinessReport.name);
      navigate(`/reports/outcome/${result.business_report_outcome_ids[0]}`);
    } else {
      toggleIsOpen(true);
    }
  };

  return (
    <div className="mb-6 text-right">
      {showVariablesModal && (
        <InjectVariablesModal
          onVariablesModalClose={onVariablesModalClose}
          businessReport={selectedBusinessReport}
          generateReport={generateReport}
        />
      )}
      {isOpen && (
        <GenerateSnapshotCollectionModal
          variables={variables}
          prompts={prompts}
          businessReport={selectedBusinessReport}
          fsEntries={selectedFsEntries}
          isOpen={isOpen}
          setIsOpen={toggleIsOpen}
        />
      )}

      <button
        className={clsx(
          "inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all",
          "border bg-midnight-blue-600 text-white shadow-sm hover:shadow",
          "border-midnight-blue-400 hover:border-midnight-blue-500 hover:bg-midnight-blue-500",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
        disabled={!selectedFiles.length || isLoading}
        onClick={handleClick}
      >
        <Sparkles size={16} />
        <span>{isLoading ? "Processing.." : "Generate SnapShot"}</span>
      </button>
    </div>
  );
};
